import { useState, useEffect } from 'react';
// @ts-ignore
import { getNewCategories, createNewCategory, updateNewCategory, deleteNewCategory } from '../../../services/newCategoryService';

interface NewsCategory {
  _id: string;
  name: string;
  description: string;
  slug: string;
  icon: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const NewsCategoriesManagement = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<NewsCategory | null>(null);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'ri-folder-line',
    order: 1,
    isActive: true
  });

  const [validationErrors, setValidationErrors] = useState({
    name: '',
    description: '',
    icon: '',
    order: ''
  });

  // Fetch categories từ API khi component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getNewCategories();
      // Sửa lại để lấy đúng path của data
      setCategories(response.data?.categories || response.categories || []);
    } catch (error) {
      console.error('Error fetching new categories:', error);
      setError('Không thể tải danh mục tin tức. Vui lòng thử lại.');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = {
      name: '',
      description: '',
      icon: '',
      order: ''
    };

    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Tên danh mục là bắt buộc';
      isValid = false;
    } else if (formData.name.length > 100) {
      errors.name = 'Tên danh mục không được vượt quá 100 ký tự';
      isValid = false;
    }

    // Validate description
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Mô tả không được vượt quá 500 ký tự';
      isValid = false;
    }

    // Validate icon
    if (!formData.icon.trim()) {
      errors.icon = 'Icon là bắt buộc';
      isValid = false;
    } else if (formData.icon.length > 100) {
      errors.icon = 'Icon không được vượt quá 100 ký tự';
      isValid = false;
    } else if (!formData.icon.startsWith('ri-')) {
      errors.icon = 'Icon phải bắt đầu với "ri-" (RemixIcon)';
      isValid = false;
    }

    // Validate order
    if (formData.order < 0) {
      errors.order = 'Thứ tự phải là số không âm';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (category: NewsCategory) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      icon: category.icon || 'ri-folder-line',
      order: category.order,
      isActive: category.isActive
    });
    setValidationErrors({ name: '', description: '', icon: '', order: '' });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa danh mục này?')) {
      try {
        await deleteNewCategory(id);
        setCategories(categories.filter(cat => cat._id !== id));
      } catch (error) {
        console.error('Error deleting new category:', error);
        alert('Có lỗi xảy ra khi xóa danh mục. Vui lòng thử lại.');
      }
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      const category = categories.find(cat => cat._id === id);
      if (category) {
        const updatedData = {
          name: category.name,
          description: category.description,
          icon: category.icon,
          order: category.order,
          isActive: !category.isActive
        };
        await updateNewCategory(id, updatedData);
        setCategories(categories.map(cat => 
          cat._id === id ? { ...cat, isActive: !cat.isActive } : cat
        ));
      }
    } catch (error) {
      console.error('Error toggling category status:', error);
      alert('Có lỗi xảy ra khi cập nhật trạng thái danh mục. Vui lòng thử lại.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon.trim(),
        order: formData.order,
        isActive: formData.isActive
      };
      
      const response = await createNewCategory(categoryData);
      const newCategory = response.data || response;
      setCategories([...categories, newCategory]);
      setIsAddModalOpen(false);
      setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
      setValidationErrors({ name: '', description: '', icon: '', order: '' });
      await fetchCategories(); // Refresh danh sách
    } catch (error: any) {
      console.error('Error creating new category:', error);
      const errorMessage = error?.response?.data?.message || 'Có lỗi xảy ra khi tạo danh mục. Vui lòng thử lại.';
      alert(errorMessage);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    if (selectedCategory) {
      try {
        const categoryData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          icon: formData.icon.trim(),
          order: formData.order,
          isActive: formData.isActive
        };
        
        const response = await updateNewCategory(selectedCategory._id, categoryData);
        const updatedCategory = response.data || response;
        
        setCategories(categories.map(cat => 
          cat._id === selectedCategory._id ? updatedCategory : cat
        ));
        setIsEditModalOpen(false);
        setSelectedCategory(null);
        setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
        setValidationErrors({ name: '', description: '', icon: '', order: '' });
        await fetchCategories(); // Refresh danh sách
      } catch (error: any) {
        console.error('Error updating new category:', error);
        const errorMessage = error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật danh mục. Vui lòng thử lại.';
        alert(errorMessage);
      }
    }
  };

  return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Quản lý danh mục tin tức</h2>
            <p className="text-gray-600">Quản lý các danh mục cho tin tức</p>
          </div>
        <button
          onClick={() => {
            setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
            setValidationErrors({ name: '', description: '', icon: '', order: '' });
            setIsAddModalOpen(true);
          }}
          className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Thêm danh mục
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="Tìm kiếm danh mục..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2A4B7C]"></div>
              <span className="text-gray-600">Đang tải...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-red-500 mb-2">
                <i className="ri-error-warning-line text-3xl"></i>
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchCategories}
                className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Thử lại
              </button>
            </div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <i className="ri-folder-line text-3xl"></i>
              </div>
              <p className="text-gray-600">
                {searchTerm ? 'Không tìm thấy danh mục nào' : 'Chưa có danh mục nào'}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Thứ tự</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Icon</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Tên danh mục</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Mô tả</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Slug</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Trạng thái</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Ngày tạo</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCategories.map((category) => (
                  <tr key={category._id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-[#2A4B7C] text-white rounded-full font-semibold">
                        {category.order}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#2A4B7C] rounded-lg">
                        <i className={`${category.icon || 'ri-folder-line'} text-lg text-white`}></i>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <h3 className="font-medium text-gray-900">{category.name}</h3>
                    </td>
                    <td className="py-4 px-6 text-gray-600">{category.description}</td>
                    <td className="py-4 px-6">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">
                        {category.slug}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={() => handleToggleActive(category._id)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          category.isActive 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {category.isActive ? 'Hoạt động' : 'Tạm dừng'}
                      </button>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      {new Date(category.createdAt).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleEdit(category)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button 
                          onClick={() => handleDelete(category._id)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Thêm danh mục mới</h3>
                <button 
                  onClick={() => {
                    setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
                    setValidationErrors({ name: '', description: '', icon: '', order: '' });
                    setIsAddModalOpen(false);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên danh mục *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (validationErrors.name) {
                        setValidationErrors({ ...validationErrors, name: '' });
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                      validationErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nhập tên danh mục"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      if (validationErrors.description) {
                        setValidationErrors({ ...validationErrors, description: '' });
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none ${
                      validationErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nhập mô tả danh mục"
                  />
                  {validationErrors.description && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon (RemixIcon class) *
                  </label>
                  <div className="flex gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2A4B7C] rounded-lg flex-shrink-0">
                      <i className={`${formData.icon} text-xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        required
                        value={formData.icon}
                        onChange={(e) => {
                          setFormData({ ...formData, icon: e.target.value });
                          if (validationErrors.icon) {
                            setValidationErrors({ ...validationErrors, icon: '' });
                          }
                        }}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                          validationErrors.icon ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="vd: ri-folder-line"
                      />
                    </div>
                  </div>
                  {validationErrors.icon && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.icon}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Xem danh sách icon tại{' '}
                    <a
                      href="https://remixicon.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2A4B7C] hover:underline"
                    >
                      remixicon.com
                    </a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thứ tự hiển thị
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => {
                      setFormData({ ...formData, order: parseInt(e.target.value) || 0 });
                      if (validationErrors.order) {
                        setValidationErrors({ ...validationErrors, order: '' });
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                      validationErrors.order ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {validationErrors.order && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.order}</p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C] cursor-pointer"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Kích hoạt danh mục
                  </label>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
                  >
                    Tạo danh mục
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
                      setValidationErrors({ name: '', description: '', icon: '', order: '' });
                      setIsAddModalOpen(false);
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Chỉnh sửa danh mục</h3>
                <button 
                  onClick={() => {
                    setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
                    setValidationErrors({ name: '', description: '', icon: '', order: '' });
                    setSelectedCategory(null);
                    setIsEditModalOpen(false);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên danh mục *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (validationErrors.name) {
                        setValidationErrors({ ...validationErrors, name: '' });
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                      validationErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nhập tên danh mục"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      if (validationErrors.description) {
                        setValidationErrors({ ...validationErrors, description: '' });
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none ${
                      validationErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nhập mô tả danh mục"
                  />
                  {validationErrors.description && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon (RemixIcon class) *
                  </label>
                  <div className="flex gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2A4B7C] rounded-lg flex-shrink-0">
                      <i className={`${formData.icon} text-xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        required
                        value={formData.icon}
                        onChange={(e) => {
                          setFormData({ ...formData, icon: e.target.value });
                          if (validationErrors.icon) {
                            setValidationErrors({ ...validationErrors, icon: '' });
                          }
                        }}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                          validationErrors.icon ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="vd: ri-folder-line"
                      />
                    </div>
                  </div>
                  {validationErrors.icon && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.icon}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Xem danh sách icon tại{' '}
                    <a
                      href="https://remixicon.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2A4B7C] hover:underline"
                    >
                      remixicon.com
                    </a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thứ tự hiển thị
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => {
                      setFormData({ ...formData, order: parseInt(e.target.value) || 0 });
                      if (validationErrors.order) {
                        setValidationErrors({ ...validationErrors, order: '' });
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                      validationErrors.order ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {validationErrors.order && (
                    <p className="mt-1 text-sm text-red-500">{validationErrors.order}</p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActiveEdit"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C] cursor-pointer"
                  />
                  <label htmlFor="isActiveEdit" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Kích hoạt danh mục
                  </label>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
                  >
                    Cập nhật
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ name: '', description: '', icon: 'ri-folder-line', order: 1, isActive: true });
                      setValidationErrors({ name: '', description: '', icon: '', order: '' });
                      setSelectedCategory(null);
                      setIsEditModalOpen(false);
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsCategoriesManagement;